import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { groupKeys } from './keys.ts'

/**
 * A group's shelf: links and files.
 *
 * Uploads take the same three steps as profile photos — ask for a slot, PUT
 * the bytes straight to storage, then tell the API it landed — so the API
 * never carries a multi-megabyte body and a slow phone cannot time out a
 * request that is also doing database work.
 */

export const resourceKeys = {
  list: (groupId: string) => [...groupKeys.detail(groupId), 'resources'] as const,
}

export function useGroupResources(groupId: string, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: resourceKeys.list(groupId),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.api.v1.groups({ id: groupId }).resources.get()
      if (error) throw error
      return data
    },
  })
}

export type GroupResource = NonNullable<ReturnType<typeof useGroupResources>['data']>[number]

function useResourceInvalidation(groupId: string) {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: resourceKeys.list(groupId) })
}

export function useAddGroupLink(groupId: string) {
  const api = useApi()
  const invalidate = useResourceInvalidation(groupId)
  return useMutation({
    mutationFn: async (body: {
      title: string
      url: string
      description?: string | null
      visibility?: 'members' | 'leaders'
    }) => {
      const { data, error } = await api.api.v1.groups({ id: groupId }).resources.link.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
}

/**
 * The whole upload, as one mutation the screen can await.
 *
 * The PUT goes to the bucket with `fetch` rather than the API client: it is a
 * different origin with its own signed URL, and sending our session headers to
 * somebody else's server would be both useless and rude.
 */
export function useUploadGroupFile(groupId: string) {
  const api = useApi()
  const invalidate = useResourceInvalidation(groupId)

  return useMutation({
    mutationFn: async (input: {
      title: string
      description?: string | null
      file: Blob
      fileName: string
      contentType: string
      visibility?: 'members' | 'leaders'
    }) => {
      const slot = await api.api.v1.groups({ id: groupId }).resources['upload-url'].post({
        contentType: input.contentType,
        fileName: input.fileName,
        sizeBytes: input.file.size,
      })
      if (slot.error) throw slot.error

      const uploaded = await fetch(slot.data.url, {
        method: 'PUT',
        headers: { 'content-type': input.contentType },
        body: input.file,
      })
      if (!uploaded.ok) {
        throw new Error(`The file did not reach storage (${uploaded.status}).`)
      }

      const { data, error } = await api.api.v1.groups({ id: groupId }).resources.file.post({
        title: input.title,
        description: input.description ?? null,
        key: slot.data.key,
        fileName: input.fileName,
        ...(input.visibility ? { visibility: input.visibility } : {}),
      })
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
}

/** Renaming, or moving between everyone in the group and its leaders only. */
export function useUpdateGroupResource(groupId: string) {
  const api = useApi()
  const invalidate = useResourceInvalidation(groupId)
  return useMutation({
    mutationFn: async ({
      resourceId,
      ...body
    }: {
      resourceId: string
      title?: string
      description?: string | null
      visibility?: 'members' | 'leaders'
    }) => {
      const { data, error } = await api.api.v1
        .groups({ id: groupId })
        .resources({ resourceId })
        .patch(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
}

export function useRemoveGroupResource(groupId: string) {
  const api = useApi()
  const invalidate = useResourceInvalidation(groupId)
  return useMutation({
    mutationFn: async (resourceId: string) => {
      const { error } = await api.api.v1.groups({ id: groupId }).resources({ resourceId }).delete()
      if (error) throw error
    },
    onSuccess: () => void invalidate(),
  })
}

/**
 * Where the bytes live. Proxied by the API rather than presigned, so the link
 * only works for someone with a session — which is the point for a group that
 * shares pastoral material.
 */
export const groupFileUrl = (baseUrl: string, groupId: string, resourceId: string) =>
  `${baseUrl}/api/v1/groups/${groupId}/resources/${resourceId}/download`
